<template>
  <div class="chat-overlay">
    <!-- Floating Action Button -->
    <div class="chat-fab" @click="toggleChat" v-if="!isOpen">
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
    </div>

    <!-- Chat Window -->
    <div class="chat-window" v-if="isOpen" ref="chatWindow" :style="windowStyle">
      <!-- Top-left resize handle -->
      <div class="resize-handle" @mousedown="startResize"></div>
      <ChatSidebar :open="isSidebarOpen" @close="onSidebarClose" />
      <div class="chat-header">
        <span
          class="icon-btn hamburger-btn"
          @click="toggleSidebar"
          :title="isSidebarOpen ? 'Close conversations' : 'Open conversations'"
          :aria-label="isSidebarOpen ? 'Close conversations' : 'Open conversations'"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </span>
        <div class="chat-title-block">
          <span class="chat-title">AI Assistant</span>
          <input
            v-if="renamingActive"
            class="chat-subtitle-input"
            type="text"
            v-model="renameText"
            ref="renameInput"
            aria-label="Rename chat"
            @keydown.enter.prevent="commitRename"
            @keydown.esc.prevent="cancelRename"
            @blur="commitRename"
            @click.stop
          />
          <span
            v-else
            class="chat-subtitle"
            :class="{ placeholder: !activeChatName }"
            :title="activeChatName ? 'Double-click to rename' : ''"
            @dblclick="startHeaderRename"
          >{{ activeChatName || 'New conversation' }}</span>
        </div>
        <span
          class="save-indicator"
          :class="'state-' + saveIndicator"
          :title="saveIndicator === 'error' ? 'Couldn\'t save — will retry on next change' : ''"
          aria-live="polite"
        >
          <svg v-if="saveIndicator === 'saving'" class="spinner" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
          </svg>
          <svg v-else-if="saveIndicator === 'ok'" class="check" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <svg v-else-if="saveIndicator === 'error'" class="warn" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </span>
        <div class="header-controls">
            <span
              @click="onNewChat"
              :title="isGenerating ? 'Cannot start a new chat while AI is generating' : 'New chat'"
              :class="['icon-btn', { 'icon-btn-disabled': isGenerating }]"
              :aria-disabled="isGenerating"
              aria-label="New chat">
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            </span>
            <span @click="toggleChat" title="Close" class="icon-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </span>
        </div>
      </div>

      <div class="chat-messages" ref="scrollContainer">
        <div v-if="history.length === 0" class="empty-state">
            <p>How can I help you today?</p>
        </div>
        
        <ChatMessage 
            v-for="msg in history" 
            :key="msg.id" 
            :message="msg" 
        />
        
        <div v-if="isGenerating" class="typing-indicator">
            <span></span><span></span><span></span>
        </div>
      </div>

      <div class="chat-input-area">
        <textarea 
            v-model="inputText" 
            placeholder="Type a message..." 
            @keydown.enter.exact.prevent="send"
        ></textarea>
        <button type="button" @click="send" :disabled="!inputText.trim() || isGenerating">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ChatMessage from './ChatMessage.vue';
import ChatSidebar from './ChatSidebar.vue';

export default {
  name: 'ChatWidget',
  components: {
    ChatMessage,
    ChatSidebar
  },
  data() {
    return {
      inputText: '',
      isResizing: false,
      windowWidth: 380,
      windowHeight: 600,
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
      isSidebarOpen: false,
      renamingActive: false,
      renameText: ''
    };
  },
  computed: {
    ...mapState('chatStore', ['isOpen', 'isGenerating', 'activeChatId', 'isLoadingChat', 'saveIndicator', 'chatList']),
    ...mapGetters('chatStore', {
        history: 'currentThread'
    }),
    activeChatName() {
        if (this.activeChatId == null) return '';
        const chat = this.chatList.find(c => c.id === this.activeChatId);
        return chat ? chat.name : '';
    },
    windowStyle() {
      return {
        width: this.windowWidth + 'px',
        height: this.windowHeight + 'px'
      };
    }
  },
  watch: {
    history() {
        this.scrollToBottom();
    }
  },
  mounted() {
      this.scrollToBottom();
  },
  methods: {
    ...mapActions('chatStore', ['toggleChat', 'sendMessage', 'sendFirstMessage', 'createChatPlaceholder', 'renameChat']),
    startHeaderRename() {
        if (this.isGenerating || this.activeChatId == null) return;
        this.renameText = this.activeChatName || '';
        this.renamingActive = true;
        this.$nextTick(() => {
            const input = this.$refs.renameInput;
            if (input) { input.focus(); input.select(); }
        });
    },
    commitRename() {
        if (!this.renamingActive) return;
        const trimmed = (this.renameText || '').trim();
        if (trimmed && trimmed !== this.activeChatName && this.activeChatId != null) {
            this.renameChat({ id: this.activeChatId, name: trimmed });
        }
        this.renamingActive = false;
        this.renameText = '';
    },
    cancelRename() {
        this.renamingActive = false;
        this.renameText = '';
    },
    async send() {
        if (!this.inputText.trim()) return;
        const text = this.inputText;
        this.inputText = '';
        if (this.activeChatId === null) {
            await this.sendFirstMessage(text);
        } else {
            await this.sendMessage(text);
        }
    },
    onNewChat() {
        if (this.isGenerating) return;
        this.createChatPlaceholder();
    },
    scrollToBottom() {
        this.$nextTick(() => {
            const container = this.$refs.scrollContainer;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    },
    startResize(e) {
      this.isResizing = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startWidth = this.windowWidth;
      this.startHeight = this.windowHeight;
      document.addEventListener('mousemove', this.doResize);
      document.addEventListener('mouseup', this.stopResize);
      e.preventDefault();
    },
    doResize(e) {
      if (!this.isResizing) return;
      // Top-left resize: moving left increases width, moving up increases height
      const deltaX = this.startX - e.clientX;
      const deltaY = this.startY - e.clientY;
      const newWidth = Math.max(280, Math.min(this.startWidth + deltaX, window.innerWidth * 0.9));
      const newHeight = Math.max(300, Math.min(this.startHeight + deltaY, window.innerHeight * 0.9));
      this.windowWidth = newWidth;
      this.windowHeight = newHeight;
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.doResize);
      document.removeEventListener('mouseup', this.stopResize);
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    onSidebarClose() {
      this.isSidebarOpen = false;
      this.$nextTick(() => {
        const ta = this.$el && this.$el.querySelector('.chat-input-area textarea');
        if (ta) ta.focus();
      });
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this.doResize);
    document.removeEventListener('mouseup', this.stopResize);
  }
};
</script>

<style scoped>
.chat-overlay {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.chat-fab {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: var(--tile-bg-color);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    transition: transform 0.2s;
}

.chat-fab:hover {
    transform: scale(1.05);
}

.chat-window {
    min-width: 280px;
    min-height: 300px;
    max-width: 90vw;
    max-height: 90vh;
    background: white;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 20px rgba(0,0,0,0.2);
    overflow: hidden;
    animation: slideUp 0.3s ease-out;
    position: relative;
}

.resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
    z-index: 10;
}

.resize-handle::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 4px;
    width: 8px;
    height: 8px;
    border-left: 2px solid rgba(255,255,255,0.6);
    border-top: 2px solid rgba(255,255,255,0.6);
}

@keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.chat-header {
    background-color: var(--tile-bg-color);
    color: white;
    padding: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
}
.chat-title-block {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    gap: 2px;
}
.chat-title {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.chat-subtitle {
    font-size: 12px;
    font-weight: 400;
    opacity: 0.85;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: text;
    user-select: none;
}
.chat-subtitle.placeholder {
    font-style: italic;
    opacity: 0.6;
    cursor: default;
}
.chat-subtitle:not(.placeholder):hover {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
}
.chat-subtitle-input {
    font-size: 12px;
    font-family: inherit;
    color: white;
    background: rgba(255,255,255,0.12);
    border: none;
    border-bottom: 1px solid rgba(255,255,255,0.6);
    outline: none;
    padding: 1px 4px;
    width: 100%;
    box-sizing: border-box;
}
.chat-subtitle-input:focus {
    border-bottom-color: white;
    background: rgba(255,255,255,0.18);
}

.header-controls {
    display: flex;
    align-items: center;
}

.hamburger-btn {
    margin-left: 0;
    margin-right: 4px;
}

.save-indicator {
    width: 20px;
    height: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: white;
    opacity: 0;
    transition: opacity 0.6s ease;
}
.save-indicator.state-saving { opacity: 0.7; transition: opacity 0.15s ease; }
.save-indicator.state-ok { opacity: 1; animation: save-fade-out 0.6s ease 1.5s forwards; }
.save-indicator.state-error { opacity: 0.9; transition: opacity 0.15s ease; }
.save-indicator.state-idle { opacity: 0; }

@keyframes save-fade-out {
    0%   { opacity: 1; }
    100% { opacity: 0; }
}

.save-indicator .spinner { animation: save-spin 0.8s linear infinite; }
@keyframes save-spin {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.icon-btn {
    cursor: pointer;
    margin-left: 15px;
    display: flex;
    align-items: center;
    opacity: 0.8;
}

.icon-btn:hover {
    opacity: 1;
}

.icon-btn-disabled {
    cursor: not-allowed;
    opacity: 0.35;
}
.icon-btn-disabled:hover {
    opacity: 0.35;
}

.chat-messages {
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    background-color: #f3f2f1;
}

.empty-state {
    text-align: center;
    color: #605e5c;
    margin-top: 50%;
    transform: translateY(-50%);
}

.chat-input-area {
    padding: 15px;
    border-top: 1px solid #e1dfdd;
    display: flex;
    gap: 10px;
    background: white;
}

.chat-input-area textarea {
    flex: 1;
    border: 1px solid #c8c6c4;
    border-radius: 4px;
    padding: 8px;
    resize: none;
    height: 40px;
    font-family: inherit;
}

.chat-input-area button {
    width: 40px;
    background: var(--tile-bg-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chat-input-area button:disabled {
    background: #c8c6c4;
    cursor: not-allowed;
}

.typing-indicator span {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #a19f9d;
    border-radius: 50%;
    margin-right: 4px;
    animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0s; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing {
    0%, 100% { transform: scale(0); }
    50% { transform: scale(1); }
}
</style>
